import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Filter, X } from "lucide-react";

const CATEGORIES = ["T-shirt", "Maglie", "Felpe", "Camicie", "Pantaloni", "Jeans", "Shorts", "Gonne", "Vestiti", "Giacche", "Cappotti", "Completi", "Tute", "Sneakers", "Scarpe", "Sandali", "Stivali", "Ciabatte", "Cappelli", "Borse", "Cinture", "Calze"];
const GENDERS = [
    { value: "neonato", label: "Neonato" },
    { value: "bambino", label: "Bambino" },
    { value: "bambina", label: "Bambina" },
    { value: "ragazzo", label: "Ragazzo" },
    { value: "ragazza", label: "Ragazza" },
    { value: "unisex", label: "Unisex" },
];
const AGE_GROUPS = ["0-3 mesi", "3-6 mesi", "6-12 mesi", "12-18 mesi", "18-24 mesi", "2-4 anni", "4-6 anni", "6-8 anni", "8-10 anni", "10-12 anni", "12-14 anni", "14-16 anni"];
const SIZES = ["XS", "S", "M", "L", "XL", "50", "56", "62", "68", "74", "80", "86", "92", "98", "104", "110", "116", "122", "128", "134", "140", "146", "152", "158", "164"];
const SEASONS = ["Primavera/Estate", "Autunno/Inverno", "Tutto l'anno"];

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterOpen, setFilterOpen] = useState(false);

    const filters = useMemo(() => ({
        q: params.get("q") || "",
        category: params.get("category") || "",
        gender: params.get("gender") || "",
        age_group: params.get("age_group") || "",
        size: params.get("size") || "",
        season: params.get("season") || "",
        on_sale: params.get("on_sale") === "true",
    }), [params]);

    useEffect(() => {
        const qp = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v && v !== false) qp.set(k, v === true ? "true" : v);
        });
        qp.set("limit", "100");
        setLoading(true);
        api.get(`/products?${qp.toString()}`).then(r => {
            setProducts(r.data);
            setLoading(false);
        });
    }, [filters]);

    const updateFilter = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value) next.delete(key);
        else next.set(key, value);
        setParams(next);
    };

    const clearFilters = () => navigate("/shop");
    const activeFilters = Object.entries(filters).filter(([k, v]) => v && v !== false && k !== "q").length;

    return (
        <div className="px-6 md:px-12 lg:px-24 py-12" data-testid="shop-page">
            <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-10">
                <div>
                    <div className="overline mb-3">Shop</div>
                    <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[color:var(--ms-charcoal)]">
                        {filters.q ? `Ricerca: "${filters.q}"` : "Tutta la collezione"}
                    </h1>
                    <p className="text-[color:var(--ms-muted)] text-sm mt-2" data-testid="shop-results-count">{products.length} capi</p>
                </div>
                <button onClick={() => setFilterOpen(!filterOpen)} className="md:hidden border border-[color:var(--ms-border)] px-5 py-3 text-xs tracking-[0.2em] uppercase flex items-center gap-2 self-start bg-[color:var(--ms-warm-white)]" data-testid="filter-toggle">
                    <Filter size={14} /> Filtri {activeFilters > 0 ? `(${activeFilters})` : ""}
                </button>
            </div>

            <div className="grid md:grid-cols-[240px_1fr] gap-10">
                <aside className={`${filterOpen ? "block" : "hidden"} md:block space-y-8 text-sm`} data-testid="shop-filters">
                    <FilterGroup label="Categoria" options={CATEGORIES} value={filters.category} onChange={v => updateFilter("category", v)} testid="filter-category" />
                    <FilterGroup label="Chi indossa" options={GENDERS.map(g => g.label)} value={filters.gender ? GENDERS.find(g => g.value === filters.gender)?.label : ""} onChange={v => {
                        const opt = GENDERS.find(g => g.label === v);
                        updateFilter("gender", opt ? opt.value : "");
                    }} testid="filter-gender" />
                    <FilterGroup label="Età" options={AGE_GROUPS} value={filters.age_group} onChange={v => updateFilter("age_group", v)} testid="filter-age" />
                    <FilterGroup label="Taglia" options={SIZES} value={filters.size} onChange={v => updateFilter("size", v)} testid="filter-size" collapsible />
                    <FilterGroup label="Stagione" options={SEASONS} value={filters.season} onChange={v => updateFilter("season", v)} testid="filter-season" />
                    <div>
                        <div className="overline mb-3">Outlet</div>
                        <label className="flex items-center gap-2 text-[color:var(--ms-charcoal)] cursor-pointer">
                            <input type="checkbox" checked={filters.on_sale} onChange={e => updateFilter("on_sale", e.target.checked ? "true" : "")} className="accent-[color:var(--ms-sage-dark)]" data-testid="filter-onsale" />
                            <span>Solo capi scontati</span>
                        </label>
                    </div>
                    {activeFilters > 0 && (
                        <button onClick={clearFilters} className="overline text-[color:var(--ms-terracotta)] link-underline flex items-center gap-1" data-testid="filter-clear">
                            <X size={12} /> RIMUOVI FILTRI
                        </button>
                    )}
                </aside>

                <div>
                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="aspect-[3/4] bg-[color:var(--ms-cream)] animate-pulse" />
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="panel py-32 text-center text-[color:var(--ms-muted)] text-sm" data-testid="shop-empty">
                            Nessun capo corrisponde a questi filtri. Modifica i filtri o iscriviti alla newsletter — nuovi arrivi ogni settimana.
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10" data-testid="shop-grid">
                            {products.map(p => <ProductCard key={p.id} product={p} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function FilterGroup({ label, options, value, onChange, testid, collapsible = false }) {
    const [expanded, setExpanded] = useState(!collapsible);
    const visible = expanded ? options : options.slice(0, 6);
    return (
        <div data-testid={testid}>
            <div className="overline mb-3">{label}</div>
            <ul className="space-y-1.5">
                {visible.map(o => (
                    <li key={o}>
                        <button
                            onClick={() => onChange(value === o ? "" : o)}
                            className={`w-full text-left text-sm py-1 ${value === o ? "text-[color:var(--ms-sage-dark)] font-medium" : "text-[color:var(--ms-charcoal)]"} hover:text-[color:var(--ms-sage-dark)] link-underline`}
                            data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                        >
                            {o}
                        </button>
                    </li>
                ))}
            </ul>
            {collapsible && options.length > 6 && (
                <button onClick={() => setExpanded(!expanded)} className="text-xs text-[color:var(--ms-sage-dark)] mt-2 link-underline">
                    {expanded ? "Mostra meno" : `+${options.length - 6} altre`}
                </button>
            )}
        </div>
    );
}
